import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fontSizes, InteractiveElement, iconWidth, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import React, { FunctionComponent, useState } from "react";
import { useTheme } from "@mui/material";

export enum ExpandableAdornmentType {
    Chevron,
    Upgrade,
}

interface ExpandableButtonProps {
    title: string;
    icon: IconProp;
    description?: string;
    disableButton?: boolean;
    showAdornment?: ExpandableAdornmentType;
    canShowAdditionalContent?: boolean;
    onClick?: (expanded: boolean) => void;
    className?: string;
    children?: React.ReactNode;
    initiallyExpanded?: boolean;
    dataTestId?: string;
}

/** Shows a title, icon and description inside a button, if clicked additional content can be shown */
const ExpandableButton: FunctionComponent<ExpandableButtonProps> = ({
    title,
    icon,
    description,
    disableButton = false,
    showAdornment = ExpandableAdornmentType.Chevron,
    /** To show the additional content (additional content are the children of the component) */
    canShowAdditionalContent = true,
    onClick,
    className,
    children,
    initiallyExpanded = false,
    dataTestId,
}) => {
    const [expanded, setExpanded] = useState(initiallyExpanded);
    const contentDisplayed = canShowAdditionalContent && expanded;
    const theme = useTheme<Theme>();
    return (
        <ExpandableButtonContainer className={className} showPointerCursor={disableButton}>
            <InteractiveElement
                className="button"
                onClick={() => {
                    const newValue = !expanded;
                    setExpanded(newValue);
                    onClick?.(newValue);
                }}
                data-testid={dataTestId}
            >
                <div className="title-container">
                    <FontAwesomeIcon className="icon" icon={icon} />
                    <span className="title">{title}</span>
                    {description && <span className="description">{description}</span>}
                </div>
                {showAdornment === ExpandableAdornmentType.Chevron && <FontAwesomeIcon icon={["fas", contentDisplayed ? "chevron-up" : "chevron-down"]} />}
                {showAdornment === ExpandableAdornmentType.Upgrade && <FontAwesomeIcon icon={["far", "circle-up"]} color={theme.primaryColors.focus} className="icon" />}
            </InteractiveElement>
            <div className="expandable-content"> {contentDisplayed && children}</div>
        </ExpandableButtonContainer>
    );
};

const ExpandableButtonContainer = styled.div<{ showPointerCursor: boolean }>`
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    flex-direction: column;
    width: 100%;

    .button {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        // Makes sure there is a gap between the text and the chevron
        gap: 10px;
        cursor: ${({ showPointerCursor }) => (showPointerCursor ? "pointer" : "default")};
    }

    .title-container {
        display: grid;
        justify-items: start;
        align-items: center;
        column-gap: 10px;
        grid-template-columns: ${iconWidth.small} auto;
    }

    .title {
        font-weight: 600;
        font-size: ${fontSizes.medium};
    }

    .description {
        text-align: start;
        grid-column-start: 2;
    }

    .icon {
        justify-self: center;
        font-size: ${fontSizes.medium};
    }

    .expandable-content {
        // Icon width + column-gap
        margin-left: 30px;
    }

    .bold {
        font-weight: 600;
    }
`;
export default ExpandableButton;
